import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS } from '../../../theme/themeConstants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import React from 'react';

import {
  useAllDriverManagement,
  useDeleteDriverManagement,
  usePatchDriverManagement,
} from '../../Query/DriverManagerQuery';
import AddDriverPage from './AddDriverPage';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useManageCmsParams } from '../ManageCMS/useManageCmsParams';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useDebounce } from '../Utils/useDounce';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface DeliveryBoyFormValues {
  id?: any;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  alternativeMobileNumber: string;
  currencyCode: string;
  countryCode: string | number;
  deliveryVehicleType: string;
  vehicleName: string;
  vehilceType: string;
  vehicleNumber: string;
  maximumOrderLimit: number | null;
  location: string;
  country: string;
  state: string;
  city: string;
  lat?: any;
  lng?: any;
  drivingLicenceNumber?: string;
  driverLicenceProof?: string;
  drivingLicenceExpiryDate?: string;
}

export const deliveryBoyAtom = atom<DeliveryBoyFormValues | null>(null);

type StoreWithSno = {
  id: string;
  sno: number;
  name: string;
  email: string;
  phone: string;
};

const ManageDriverTable = () => {
  const setDeliveryBoyAtom = useSetAtom(deliveryBoyAtom);
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: driverData, isLoading, refetch } = useAllDriverManagement(memoizedParams);

  const { mutate: patchDriverManagement } = usePatchDriverManagement();

  const { mutate: deleteDriverManagement } = useDeleteDriverManagement();

  const filteredRows = React.useMemo(() => {
    const storeOwners = driverData?.data || [];
    // Add sno field to each row and ensure all required fields are present
    return storeOwners.map((row : any, index:any) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.deliverBoyId,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.firstName + ' ' + row.lastName,
        email: row.email,
        phone: `${row.mobileNumber}`,
      };

      return rowWithSno as StoreWithSno;
    });
  }, [params.search, driverData, params.page, params.limit]);



  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleBlockClick = async () => {

    if (selectedRows.length === 0) {
      showMessage('Please select at least one driver to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchDriverManagement({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one driver to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchDriverManagement({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one driver to delete', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      // deleteDriverManagement(selectedRows);
      deleteDriverManagement(selectedRows, {
      onSuccess: () => {
        setSelectedRows([]);
        showMessage('driver deleted successfully', 'success');

        // Refetch data to update the table
        refetch();
      },
      onError: () => {
        showMessage('Error deleting driver', 'error');
        // If there's an error, we might want to restore the selection
        // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
      },
      });
    }
  }, [selectedRows]);

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this delivery boy?'
    );
    if (!confirmed) return;

    await patchDriverManagement({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this delivery boy'
    );
    if (!confirmed) return;

    await patchDriverManagement({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteDriverManagement([id]);
    },
    [selectedRows, deleteDriverManagement]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: any) => {
    setDeliveryBoyAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setDeliveryBoyAtom(null);
  };

  // Determine which bulk buttons to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = driverData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.deliverBoyId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedRows, driverData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('deliveryBoy', 'Edit');
    const canDelete = hasPermission('deliveryBoy', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditClick(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDeleteButton(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.2, // small number
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2, // moderately sized
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5, // moderately sized
    },
    {
      field: 'phone',
      headerName: 'Delivery Boy Phone',
      flex: 1.5, // usually a long string
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id as string;
        const canBlock = hasPermission('deliveryBoy', 'Block');
        const canUnblock = hasPermission('deliveryBoy', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton size="small" sx={{ color: isBlocked ? '#f44336' : '#4caf50' }} onClick={() => isBlocked ? handleUnBlockClickButton(id) : handleBlockClickButton(id)}>
            {isBlocked ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by name, email, mobile"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <PermissionActionButtons
              module="deliveryBoy"
              onBlock={showBlock ? handleBlockClick : undefined}
              onUnblock={showUnblock ? handleUnBlockClick : undefined}
              onDelete={selectedRows.length ? handleDeleteClick : undefined}
            />
            {hasPermission('deliveryBoy', 'Add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Driver
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Delivery boy"
        >
          <AddDriverPage onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={
            modalContent === 'add' ? 'Add Delivery boy' : 'Edit Delivery boy'
          }
        >
          <AddDriverPage onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={driverData?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
          selectionModel={selectedRows}
        />
      </Box>
    </>
  );
};

export default ManageDriverTable;
